import { capitalize } from '@gettactic/helpers/src/strings/capitalize';
import { IconValue } from '../../../components';
import {
  DEFAULT_FONT_FAMILY,
  DEFAULT_NODE_TEXT_COLOR,
  DEFAULT_TEXT_SHAPE_COLOR,
  DEFAULT_TEXT_SHAPE_FONT_SIZE
} from '../components/map/defines';
import { FURNITURE_LIST, Furniture } from '../furniture-list';
import {
  Shape,
  SpotLabelPlacement,
  SpotOrientation,
  SpotShapeType
} from '../types';
import { getRandomColor } from './colors';
import { generateShapeId } from './generateShapeId';
import {
  createFurnitureShape,
  createIconShape,
  createImageShape,
  createSvgShape
} from './svg';

interface ShapePosition {
  x?: number;
  y?: number;
  text?: string;
}

export const createRectangle = (position?: ShapePosition): Shape => ({
  id: generateShapeId('rectangle'),
  type: 'rectangle',
  x: position?.x || 0,
  y: position?.y || 0,
  width: 100,
  height: 100,
  color: getRandomColor(),
  layerType: 'shapes'
});

export const createCircle = (position?: ShapePosition): Shape => ({
  id: generateShapeId('circle'),
  type: 'circle',
  x: position?.x || 0,
  y: position?.y || 0,
  width: 50,
  height: 50,
  radius: 25,
  color: getRandomColor(),
  layerType: 'shapes'
});

export const createText = (position?: ShapePosition): Shape => ({
  id: generateShapeId('text'),
  type: 'text',
  x: position?.x || 0,
  y: position?.y || 0,
  width: 200,
  height: 16,
  textColor: DEFAULT_TEXT_SHAPE_COLOR,
  text: 'Double click to edit',
  fontSize: DEFAULT_TEXT_SHAPE_FONT_SIZE,
  fontFamily: DEFAULT_FONT_FAMILY,
  layerType: 'shapes'
});

export const createIconElement = (
  position?: ShapePosition,
  icon?: IconValue,
  text?: string
): Shape => {
  return createIconShape(
    {
      x: position?.x || 0,
      y: position?.y || 0,
      text
    },
    icon || { icon: 'Star', set: 'lucide' }
  );
};

export const createFurnitureElement = (
  position?: ShapePosition,
  furniture?: Furniture
): Shape => {
  return createFurnitureShape(
    {
      x: position?.x || 0,
      y: position?.y || 0
    },
    furniture || FURNITURE_LIST[0]
  );
};

export const createSvgElement = (
  position?: ShapePosition,
  path?: string,
  content?: string,
  width?: number,
  height?: number
): Shape =>
  createSvgShape(
    {
      x: position?.x || 0,
      y: position?.y || 0,
      width,
      height
    },
    path,
    content
  );

export const createImageElement = (
  position?: ShapePosition,
  path?: string,
  width?: number,
  height?: number
): Shape =>
  createImageShape(
    {
      x: position?.x || 0,
      y: position?.y || 0,
      width,
      height
    },
    path
  );

export const createNodeShape = (position?: ShapePosition): Shape => {
  const x = position?.x || 0;
  const y = position?.y || 0;
  const text = position?.text || '';

  const type = 'spot';

  return {
    id: generateShapeId(type),
    type,
    spotAppearance: undefined,
    orientation: 'top' as SpotOrientation,
    labelPlacement: 'on-rect' as SpotLabelPlacement,
    spotType: 'desk' as SpotShapeType,
    textAlign: 'center',
    x,
    y,
    label: text,
    textColor: DEFAULT_NODE_TEXT_COLOR,
    name: `${capitalize(type)} ${text}`,
    width: 100,
    height: 100,
    spacing: 2,
    rowSpacing: 5,
    rowsExtraSpacing: 20,
    layerType: 'spots',
    rectProps: {
      cornerRadius: 2,
      opacity: 1
    }
  };
};
